'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import NextImage from '@/features/Image';
import Link from 'next/link';
import { useRef } from 'react';
import { pushPromotionEvent } from '@/features/gtm/gtmUtils';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/autoplay';

interface SliderImage {
  id?: string | number;
  name?: string;
  photo: string;
  link: string;
}

interface BannerSliderProps {
  sliders: SliderImage[];
}

const BannerSlider: React.FC<BannerSliderProps> = ({ sliders }) => {
  const viewedPromotionIds = useRef(new Set<string>());
  const isOptimizationDisabled =
    process.env.NEXT_PUBLIC_DISABLE_IMAGE_OPTIMIZATION === 'true';

  const getPromotion = (index: number) => {
    const image = sliders?.[index];
    if (!image) return null;

    const id = String(image.id ?? `${image.link}-${index + 1}`);
    return {
      id,
      name: image.name ?? `Homepage Hero ${index + 1}`,
      creativeName: image.name ?? `Homepage Hero ${index + 1}`,
      creativeSlot: `homepage_hero_${index + 1}`,
    };
  };

  const trackPromotionView = (index: number) => {
    const promotion = getPromotion(index);
    if (!promotion || viewedPromotionIds.current.has(promotion.id)) return;

    viewedPromotionIds.current.add(promotion.id);
    pushPromotionEvent('view_promotion', promotion);
  };

  const isInternalLink = (url: string) => {
    try {
      if (url.startsWith('/')) return true;
      const parsedUrl = new URL(url);
      return parsedUrl.hostname.includes('kireibd.com');
    } catch {
      return true;
    }
  };

  return (
    <Swiper
      modules={[Autoplay, EffectFade]}
      effect="fade"
      fadeEffect={{ crossFade: true }}
      loop={true}
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
      }}
      onSwiper={(swiper) => trackPromotionView(swiper.realIndex)}
      onSlideChange={(swiper) => trackPromotionView(swiper.realIndex)}
      speed={1000}
      className="banner-swiper"
    >
      {sliders?.map((image, index) => {
        const internal = isInternalLink(image.link);

        return (
          <SwiperSlide key={index}>
            <div className="banner-slide">
              <Link
                href={image.link}
                target={internal ? '_self' : '_blank'}
                rel={internal ? undefined : 'noopener noreferrer'}
                onClick={() => {
                  const promotion = getPromotion(index);
                  if (promotion) {
                    pushPromotionEvent('select_promotion', promotion);
                  }
                }}
              >
                <NextImage
                  src={image.photo}
                  alt="Kirei banner image"
                  width={1920}
                  height={800}
                  priority={index === 0}
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  decoding={index === 0 ? 'sync' : 'async'}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  sizes="(max-width: 768px) 100vw, 80vw"
                  className="img-fluid w-100 rounded-3 banner__thumbnail"
                  unoptimized={isOptimizationDisabled}
                />
              </Link>
            </div>
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
};

export default BannerSlider;
